<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Support</h1>
            <p class="text-slate-400">Get help with your company account, features, and technical issues</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="openLiveChat" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
              Start Live Chat
            </button>
            <button @click="createTicket" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Create Ticket
            </button>
          </div>
        </div>
      </div>

      <!-- Quick Help Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-700/50 transition-colors cursor-pointer" @click="navigateToSection('faq')">
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white">FAQ</h3>
              <p class="text-sm text-slate-400">Find answers to common questions</p>
            </div>
          </div>
        </div>

        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-700/50 transition-colors cursor-pointer" @click="navigateToSection('contact')">
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white">Contact Us</h3>
              <p class="text-sm text-slate-400">Get in touch with our team</p>
            </div>
          </div>
        </div>

        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-700/50 transition-colors cursor-pointer" @click="navigateToSection('tickets')">
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white">My Tickets</h3>
              <p class="text-sm text-slate-400">View your support tickets</p>
            </div>
          </div>
        </div>

        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-700/50 transition-colors cursor-pointer" @click="navigateToSection('resources')">
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white">Resources</h3>
              <p class="text-sm text-slate-400">Guides and documentation</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- FAQ Section -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6" id="faq">
            <h2 class="text-xl font-semibold text-white mb-6">Frequently Asked Questions</h2>
            
            <div class="space-y-4">
              <div v-for="(faq, index) in faqs" :key="index" class="border border-slate-700/50 rounded-lg">
                <button 
                  @click="toggleFaq(index)" 
                  class="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                >
                  <span class="text-sm font-medium text-white">{{ faq.question }}</span>
                  <svg 
                    :class="['w-5 h-5 text-slate-400 transition-transform', faq.open ? 'rotate-180' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                <div v-if="faq.open" class="px-4 pb-3">
                  <p class="text-sm text-slate-300">{{ faq.answer }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Form -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-8" id="contact">
            <h2 class="text-xl font-semibold text-white mb-6">Contact Support</h2>
            
            <form @submit.prevent="submitContactForm" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                  <input v-model="contactForm.firstName" type="text" class="form-input w-full" required />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                  <input v-model="contactForm.lastName" type="text" class="form-input w-full" required />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input v-model="contactForm.email" type="email" class="form-input w-full" required />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                  <input v-model="contactForm.phone" type="tel" class="form-input w-full" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Subject</label>
                <select v-model="contactForm.subject" class="form-select w-full" required>
                  <option value="">Select a subject</option>
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing Question</option>
                  <option value="feature">Feature Request</option>
                  <option value="account">Account Management</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Message</label>
                <textarea v-model="contactForm.message" class="form-input w-full" rows="5" required></textarea>
              </div>

              <div class="flex items-center justify-between">
                <label class="flex items-center">
                  <input v-model="contactForm.urgent" type="checkbox" class="form-checkbox" />
                  <span class="ml-3 text-sm text-slate-300">Mark as urgent</span>
                </label>
                
                <button type="submit" class="btn-primary">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                  Send Message
                </button>
              </div>
            </form>
          </div>

          <!-- Support Tickets -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-8" id="tickets">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">My Support Tickets</h2>
              <button @click="createTicket" class="btn-secondary">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                New Ticket
              </button>
            </div>
            
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-slate-700/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Ticket ID</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Subject</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Created</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-700/50">
                  <tr v-for="ticket in supportTickets" :key="ticket.id" class="hover:bg-slate-700/30">
                    <td class="px-4 py-3 whitespace-nowrap text-sm text-white">#{{ ticket.id }}</td>
                    <td class="px-4 py-3 whitespace-nowrap text-sm text-white">{{ ticket.subject }}</td>
                    <td class="px-4 py-3 whitespace-nowrap">
                      <span :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        ticket.status === 'open' ? 'bg-emerald-500/20 text-emerald-400' :
                        ticket.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      ]">
                        {{ ticket.status }}
                      </span>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{{ formatDate(ticket.createdAt) }}</td>
                    <td class="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <button @click="viewTicket(ticket.id)" class="text-emerald-400 hover:text-emerald-300 mr-3">
                        View
                      </button>
                      <button @click="closeTicket(ticket.id)" class="text-red-400 hover:text-red-300">
                        Close
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Contact Information -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Contact Information</h3>
            
            <div class="space-y-4">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-white">support@oilbuddyai.com</p>
                  <p class="text-xs text-slate-400">Email Support</p>
                </div>
              </div>
              
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-white">1-800-OILBUDDY</p>
                  <p class="text-xs text-slate-400">Phone Support</p>
                </div>
              </div>
              
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-white">Live Chat</p>
                  <p class="text-xs text-slate-400">24/7 Available</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Support Hours -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Support Hours</h3>
            
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Monday - Friday</span>
                <span class="text-sm text-white">8:00 AM - 8:00 PM EST</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Saturday</span>
                <span class="text-sm text-white">9:00 AM - 6:00 PM EST</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Sunday</span>
                <span class="text-sm text-white">10:00 AM - 4:00 PM EST</span>
              </div>
              <div class="border-t border-slate-700/50 pt-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-emerald-400 font-medium">Emergency Support</span>
                  <span class="text-sm text-emerald-400">24/7 Available</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Resources -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6" id="resources">
            <h3 class="text-lg font-semibold text-white mb-4">Quick Resources</h3>
            
            <div class="space-y-3">
              <a href="#" @click.prevent="downloadGuide('getting-started')" class="resource-link">
                <svg class="w-5 h-5 text-cyan-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">Getting Started Guide</span>
              </a>
              
              <a href="#" @click.prevent="downloadGuide('user-manual')" class="resource-link">
                <svg class="w-5 h-5 text-emerald-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">User Manual</span>
              </a>
              
              <a href="#" @click.prevent="downloadGuide('api-docs')" class="resource-link">
                <svg class="w-5 h-5 text-purple-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">API Documentation</span>
              </a>
              
              <a href="#" @click.prevent="downloadGuide('safety-guide')" class="resource-link">
                <svg class="w-5 h-5 text-amber-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">Safety Guidelines</span>
              </a>
            </div>
          </div>

          <!-- Live Chat Widget -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Live Chat</h3>
            
            <div class="space-y-4">
              <div class="flex items-center space-x-3">
                <div class="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                <span class="text-sm text-emerald-400">Online Now</span>
              </div>
              
              <div class="text-sm text-slate-300 mb-4">
                <p>Average response time: <span class="text-white font-medium">2 minutes</span></p>
              </div>
              
              <button @click="openLiveChat" class="w-full btn-primary">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
                Start Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const faqs = ref([
  {
    question: 'How do I add new employees to my company?',
    answer: 'You can add new employees by going to the Crew Management page and clicking "Add Crew Member". Fill in their details and they will receive an invitation email.',
    open: false
  },
  {
    question: 'How do I process payroll for my employees?',
    answer: 'Navigate to the Payroll page where you can view all employees, their hours, and process payments. You can also set up automatic payroll processing.',
    open: false
  },
  {
    question: 'Can I integrate with my existing accounting software?',
    answer: 'Yes! We support integrations with QuickBooks, Xero, and other popular accounting platforms. Go to Settings > Integrations to set this up.',
    open: false
  },
  {
    question: 'How do I post a new job listing?',
    answer: 'Go to the Post Job page and fill out the job details form. You can save as draft or publish immediately. The job will appear in the contractor marketplace.',
    open: false
  },
  {
    question: 'What safety features are available?',
    answer: 'We provide comprehensive safety management including incident reporting, training schedules, compliance checklists, and real-time safety alerts.',
    open: false
  },
  {
    question: 'How do I track expenses and reimbursements?',
    answer: 'Use the Expenses page to log company expenses, track reimbursements, and generate expense reports. You can also upload receipts for documentation.',
    open: false
  }
])

const contactForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  urgent: false
})

const supportTickets = ref([])

// Methods
const toggleFaq = (index: number) => {
  faqs.value[index].open = !faqs.value[index].open
}

const navigateToSection = (section: string) => {
  const element = document.getElementById(section)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

const openLiveChat = () => {
  // Implement live chat functionality
  console.log('Opening live chat...')
}

const createTicket = () => {
  // Navigate to ticket creation or open modal
  console.log('Creating new ticket...')
}

const submitContactForm = async () => {
  try {
    const response = await fetch('/api/company/contact-support', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(contactForm.value)
    })
    
    if (response.ok) {
      console.log('Contact form submitted successfully')
      // Reset form
      contactForm.value = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        urgent: false
      }
    }
  } catch (error) {
    console.error('Error submitting contact form:', error)
  }
}

const fetchSupportTickets = async () => {
  try {
    const response = await fetch('/api/company/support-tickets', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      supportTickets.value = data.tickets || []
    }
  } catch (error) {
    console.error('Error fetching support tickets:', error)
  }
}

const viewTicket = (ticketId: number) => {
  // Navigate to ticket details
  console.log('Viewing ticket:', ticketId)
}

const closeTicket = async (ticketId: number) => {
  try {
    const response = await fetch(`/api/company/close-ticket/${ticketId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      await fetchSupportTickets()
    }
  } catch (error) {
    console.error('Error closing ticket:', error)
  }
}

const downloadGuide = (guide: string) => {
  // Implement guide download
  console.log('Downloading guide:', guide)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await fetchSupportTickets()
})
</script>

<style scoped>
.resource-link {
  @apply flex items-center w-full p-3 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}

.form-checkbox {
  @apply w-4 h-4 text-emerald-500 bg-slate-700/50 border-slate-600/50 rounded focus:ring-emerald-500 focus:ring-2;
}
</style>
